import { axisTicks } from '../../domain/cars/accelerationTrace';
import {
    axisPath, captionAnchors, gridLines, tickAnchors,
} from '../../domain/charts/plotFrame';

/**
 * The acceleration band's geometry — everything that turns a speed and a time
 * into a position in the plot (SPEC-004 PR-4.5).
 *
 * Split out of `CarAccelerationChart.js` when moving the axis captions inside
 * the SVG took that file past the repo's 350-line ceiling, and split at the seam
 * `valueChartGeometry.js` already marks for the band below: the component is
 * markup and copy, and every line here is arithmetic with no opinion about what
 * it draws. It is the same division `accelerationTrace.js` makes one layer up —
 * that module owns the arithmetic of the DATA, this one owns the arithmetic of
 * the PICTURE, and neither needs React.
 *
 * The FRAME itself is not here. Where a gridline falls, where a tick number sits
 * against it and where a caption goes come from `domain/charts/plotFrame.js`,
 * which the share card reads too — this module decides the box and the
 * projection, and `frameFor` hands both to the shared definition. That is the
 * whole reason the band and the card cannot drift again.
 */

/**
 * The drawing box, in viewBox units.
 *
 * A viewBox rather than measured pixels: the band is full-bleed on a phone and
 * inset on a wide screen, and a chart that had to know which would need a
 * resize observer to draw a straight line. The PLOT insets leave room for the
 * tick labels, which live outside the box in this shape — the C64 screen draws
 * them in the margin and so does this.
 */
export const VIEW = { width: 320, height: 180 };

/**
 * The same chart drawn on a bigger sheet of paper.
 *
 * NOT a bigger chart. A `viewBox` scales everything inside it, so the phone's
 * 320-unit frame stretched across a desktop panel magnifies the 9-unit tick
 * numerals and the 1-unit gridlines along with the trace — the drawing arrives
 * at three or four times the weight it was designed at, which reads as clumsy
 * rather than as large. It also grows proportionally TALLER, which is what
 * pushed the graph off the first screen.
 *
 * Doubling the units and adding only a third to the height does the opposite:
 * every fixed-size thing in here — type, strokes, marks, label boxes — becomes
 * half as heavy relative to the frame, and the plot gains real width to spend
 * on the time axis. Same drawing, finer.
 *
 * The phone keeps `VIEW` untouched, which also keeps the 1200x630 share card
 * untouched: it reads this module through `frameFor` and never asks for wide.
 */
export const WIDE_VIEW = { width: 660, height: 240 };
// `top` is wide enough to seat a LABEL above the highest mark, not just the mark
// itself. The last measurement sits near the ceiling by construction, its figures
// are drawn above it, and at a ten-unit inset the speed line landed at y=6 with
// its ascender outside the viewBox — so the one number that says how far the test
// went was the one number with its digits shaved off.
//
// `left` is 40 rather than the 34 it was, and the six units are the caption
// column: the speed caption is turned on its side in the gutter now, and three
// digits of km/h have to clear it. Forty is also what `valueChartGeometry.js`
// uses, which is the other half of the reason — the two bands stack on one page
// and a plot indented six units from the one above it reads as a mistake.
const PLOT = { left: 40, right: 8, top: 20, bottom: 22 };

/**
 * The wide frame's gutters, which are NOT the narrow ones scaled.
 *
 * The insets exist to seat things whose size is fixed in user units — three
 * digits of km/h beside the turned caption, a label above the highest mark — and
 * those did not get bigger when the frame did. Scaling the gutters with the box
 * would reserve twice the room for the same numerals and leave the plot floating
 * in a margin. They grow a little, because the axis now carries more ticks, and
 * no more than that.
 */
const WIDE_PLOT = { left: 52, right: 16, top: 22, bottom: 28 };

/**
 * The type size of the tick numbers and the captions, in viewBox units.
 *
 * DECLARED HERE AND SET AS AN ATTRIBUTE rather than left to the stylesheet,
 * because the geometry needs the same number: `tickAnchors` centres a figure
 * optically against its gridline and `captionAnchors` puts the caption's spine
 * a character-width into the gutter, and both are fractions of this. Two copies
 * — one in CSS, one here — is `--duration-deal`'s problem exactly (SPEC-004
 * D-9), and the symptom would be tick numbers sitting a fraction off the lines
 * they label, which reads as a chart drawn carelessly rather than as a bug.
 *
 * A `px` length inside a viewBox IS a user unit, so this is the same 9 the
 * stylesheet used to carry.
 */
export const TICK_SIZE = 9;

/**
 * The drawable rectangle inside a frame, as the two numbers every projection
 * needs. A function rather than two constants because there are two frames now,
 * and a module-level pair would silently be the phone's on both.
 */
function plotBox(view, plot) {
    return {
        width: view.width - plot.left - plot.right,
        height: view.height - plot.top - plot.bottom,
    };
}

/**
 * How far past the last measurement the axes run.
 *
 * Not decoration. Scaled exactly to the measurements, the final point lands ON
 * the frame and its mark is drawn half outside the plot — so the one point that
 * says how far the test actually went is the one point that reads as clipped.
 * A twentieth of the axis is enough to seat it and little enough that the chart
 * still fills itself; the tick labels stay keyed to the measured extent, so
 * nothing here invents a gridline either.
 */
const HEADROOM = { speed: 1.08, time: 1.05 };

/**
 * The measurement labels, in viewBox units. `width` and `height` are what the
 * crowding test measures against rather than what the type is set at — a label
 * is two short lines of tabular figures, so its box is predictable enough to
 * lay out arithmetically and never needs to be measured in the DOM.
 */
const LABEL = {
    offset: 6,
    width: 40,
    height: 9,
    /**
     * How far a displaced label moves, and it is NOT two line-heights.
     *
     * A label is two lines nine apart, so its block runs from one ascender above
     * the upper line to just below the lower one — nineteen units, not eighteen.
     * Moving by eighteen leaves the two blocks touching by a single unit, which
     * is invisible in a fixture of one car and shows up on the Viper, the only
     * car in the pack with a crowded pair AND a fourth point after it.
     */
    clear: 21,
};

/** Data space to viewBox space. One multiply per axis, and the y axis flips. */
function projector({ duration, ceiling }, plot = PLOT, box = plotBox(VIEW, PLOT)) {
    const seconds = duration * HEADROOM.time;
    const top = ceiling * HEADROOM.speed;

    return {
        x: (value) => plot.left + (value / seconds) * box.width,
        y: (value) => plot.top + box.height - (value / top) * box.height,
    };
}

/**
 * Where each measurement's own figures go.
 *
 * ABOVE AND LEFT of its mark, which is not an arbitrary corner: these curves
 * rise left to right and bend over, so the region above the line is the empty
 * one, and a label placed there sits on the plate rather than across the trace.
 *
 * The nudge is for the two cars whose measurements crowd. The NSX was timed to
 * 100 mph at 12.1 s and through the quarter at 13.7 s — one and a half seconds
 * and ten km/h apart — so their labels land on each other, and the Corvette's
 * third and fourth do the same. Lifting the later one clear is enough because
 * the crowding is always a PAIR: these tests published four points at most, and
 * no car in the pack has three of them close together.
 *
 * Returns BOTH baselines — `y` for the time and `speedY` for the speed one line
 * above it. The component used to subtract `LABEL.height` itself, which meant
 * the markup knew the label's internal leading; now the only thing that knows a
 * label is two lines is the module that decides where the two lines go.
 */
export function labelPositions(measured, project, plot = PLOT) {
    const placed = [];

    measured.forEach((point) => {
        const x = project.x(point.seconds) - LABEL.offset;
        const resting = project.y(point.kmh) - LABEL.offset;
        const previous = placed[placed.length - 1];
        const crowded = previous
            && Math.abs(x - previous.x) < LABEL.width
            && Math.abs(resting - previous.y) < LABEL.clear;
        const lifted = previous ? previous.y - LABEL.clear : resting;

        placed.push({
            point,
            x,
            speedY: 0, // replaced below, once `y` is known
            // Lifting is the preferred escape, but a crowded pair near the top of
            // the plot has nowhere above to go — the NSX's last two points are at
            // 161 and 171 km/h against a ceiling of 171, so the lift would put a
            // figure outside the frame. Those drop BELOW the label they clash
            // with instead, into the open ground under a curve that has already
            // flattened. Only one of the two directions is ever needed, because
            // no test in the pack published three points close together.
            // eslint-disable-next-line no-nested-ternary
            y: !crowded
                ? resting
                : (lifted >= plot.top + LABEL.height ? lifted : previous.y + LABEL.clear),
        });

        const just = placed[placed.length - 1];

        just.speedY = just.y - LABEL.height;
    });

    return placed;
}

/**
 * The curve as one `d`.
 *
 * Straight segments between SAMPLES, which is not the same thing as straight
 * segments between measurements: the domain has already sampled the smooth curve
 * finely enough that a polyline through its samples reads as the curve. Doing it
 * this way rather than emitting cubic commands keeps the arithmetic that decides
 * the shape in one testable place instead of splitting it across a path string.
 */
export function curvePath(curve, project) {
    return curve
        .map((sample, index) => `${index === 0 ? 'M' : 'L'}${project.x(sample.seconds).toFixed(2)} ${project.y(sample.kmh).toFixed(2)}`)
        .join(' ');
}

/**
 * The whole frame for one trace: the box, the projection, and every position
 * the shared definition derives from them.
 *
 * Assembled here rather than in the component so the component never holds a
 * plot rect and a projection at the same time as the markup that consumes them
 * — and so a test can ask where the frame put something without rendering.
 */
export function frameFor(trace, wide = false) {
    const view = wide ? WIDE_VIEW : VIEW;
    const insets = wide ? WIDE_PLOT : PLOT;
    const box = plotBox(view, insets);
    const project = projector(trace, insets, box);
    const plot = {
        x: insets.left, y: insets.top, width: box.width, height: box.height,
    };
    const ladders = { xTicks: axisTicks(trace.duration), yTicks: axisTicks(trace.ceiling) };

    return {
        // The view travels WITH the frame so the component never has to decide
        // which `viewBox` goes with which projection — picking one and drawing
        // the other is a chart that is subtly, unfixably wrong.
        view,
        insets,
        project,
        plot,
        grid: gridLines({ plot, project, ...ladders }),
        ticks: tickAnchors({
            plot, project, ...ladders, tickSize: TICK_SIZE,
        }),
        axis: axisPath(plot),
        // `gutterLeft` is the viewBox origin: the band has no panel padding of
        // its own, because the plate it sits on is drawn by CSS around the whole
        // figure rather than as a rect inside the SVG.
        captions: captionAnchors({ plot, gutterLeft: 0, tickSize: TICK_SIZE }),
    };
}
